<template>
    <!-- Combobox con buscador (código de país, país de origen, ciudad).
         Teclado: ↑ ↓ Re Pág Av Pág Enter Esc Tab; escribir una letra con el
         combo cerrado lo abre y empieza a buscar. -->
    <div
        class="nf-combo"
        :class="{ 'is-open': open, 'has-value': !!selected, 'drop-up': dropUp }"
    >
        <button
            :id="uid + '-btn'"
            ref="trigger"
            type="button"
            class="nf-combo-trigger"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-labelledby="uid + '-lbl ' + uid + '-btn'"
            @click="open ? close(true) : openPop()"
            @keydown="onTriggerKey"
        >
            <span :id="uid + '-lbl'" class="nf-combo-label">{{ label }}</span>
            <span v-if="selected" class="nf-combo-value">
                <NfFlag :code="selected.flag" />
                <span>{{ showMeta && selected.meta ? selected.meta : selected.label }}</span>
            </span>
            <svg class="nf-combo-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>

        <transition name="nf-pop">
            <div v-if="open" class="nf-combo-pop">
                <div class="nf-combo-search">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                    <input
                        ref="search"
                        v-model="query"
                        type="text"
                        :placeholder="searchPlaceholder"
                        autocomplete="off"
                        spellcheck="false"
                        role="combobox"
                        aria-expanded="true"
                        :aria-controls="uid + '-list'"
                        aria-autocomplete="list"
                        :aria-activedescendant="filtered[active] ? uid + '-o' + active : null"
                        @keydown="onKey"
                    >
                </div>
                <ul :id="uid + '-list'" ref="list" class="nf-combo-list" role="listbox" :aria-labelledby="uid + '-lbl'" tabindex="-1">
                    <li v-if="!filtered.length" class="nf-combo-empty">{{ emptyText }}</li>
                    <template v-for="(o, i) in filtered">
                        <li v-if="o.group && (i === 0 || filtered[i - 1].group !== o.group)" :key="'g-' + o.group" class="nf-combo-group" role="presentation">{{ o.group }}</li>
                        <li
                            :id="uid + '-o' + i"
                            :key="'o-' + o.value"
                            class="nf-combo-option"
                            :class="{ 'is-active': i === active }"
                            role="option"
                            :aria-selected="o.value === value ? 'true' : 'false'"
                            @mousemove="active = i"
                            @click="pick(o)"
                        >
                            <NfFlag :code="o.flag" />
                            <span>{{ o.label }}</span>
                            <span v-if="o.meta" class="nf-opt-meta">{{ o.meta }}</span>
                        </li>
                    </template>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script>
import NfFlag from './NfFlag.vue'

let seq = 0
const norm = (s) => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export default {
    name: 'NfCombo',
    components: { NfFlag },
    props: {
        // [{ value, label, group?, flag?, meta? }]
        options: { type: Array, required: true },
        value: { type: [String, Number], default: '' },
        label: { type: String, required: true },
        searchPlaceholder: { type: String, default: '' },
        emptyText: { type: String, default: '' },
        // En el botón muestra `meta` (p. ej. "+54") en vez del nombre.
        showMeta: { type: Boolean, default: false },
    },
    data() {
        return { uid: 'nfc' + (++seq), open: false, query: '', active: 0, dropUp: false }
    },
    computed: {
        selected() {
            return this.options.find((o) => o.value === this.value) || null
        },
        filtered() {
            const q = norm(this.query.trim()).replace(/^\+/, '')
            if (!q) return this.options
            return this.options.filter((o) => norm(`${o.label} ${o.group || ''} ${o.meta || ''}`).includes(q))
        },
    },
    watch: {
        query() { this.setActive(0) },
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocClick)
    },
    methods: {
        openPop(seed) {
            if (this.open) return
            this.$emit('open', this)
            // ¿Abre hacia arriba? Solo si abajo no entra y arriba sí.
            const r = this.$el.getBoundingClientRect()
            const below = window.innerHeight - r.bottom
            this.dropUp = below < 340 && r.top > below
            this.query = typeof seed === 'string' ? seed : ''
            this.open = true
            this.$nextTick(() => {
                const idx = this.filtered.findIndex((o) => o.value === this.value)
                this.setActive(idx >= 0 ? idx : 0)
                if (this.$refs.search) this.$refs.search.focus({ preventScroll: true })
                setTimeout(() => document.addEventListener('click', this.onDocClick), 0)
            })
        },
        close(focusTrigger) {
            if (!this.open) return
            this.open = false
            document.removeEventListener('click', this.onDocClick)
            if (focusTrigger && this.$refs.trigger) this.$refs.trigger.focus({ preventScroll: true })
        },
        onDocClick(e) {
            if (!this.$el.contains(e.target)) this.close(false)
        },
        onTriggerKey(e) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault()
                this.openPop()
            } else if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault()
                this.openPop(e.key)
            }
        },
        onKey(e) {
            const n = this.filtered.length
            if (e.key === 'ArrowDown') { e.preventDefault(); this.setActive(Math.min(n - 1, this.active + 1)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive(Math.max(0, this.active - 1)) }
            else if (e.key === 'PageDown') { e.preventDefault(); this.setActive(Math.min(n - 1, this.active + 8)) }
            else if (e.key === 'PageUp') { e.preventDefault(); this.setActive(Math.max(0, this.active - 8)) }
            else if (e.key === 'Enter') { e.preventDefault(); if (this.filtered[this.active]) this.pick(this.filtered[this.active]) }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(true) }
            else if (e.key === 'Tab') { this.close(false) }
        },
        setActive(i) {
            this.active = i
            this.$nextTick(() => {
                const li = this.$refs.list && this.$refs.list.querySelector('#' + this.uid + '-o' + i)
                if (li) li.scrollIntoView({ block: 'nearest' })
            })
        },
        pick(o) {
            this.close(true)
            this.$emit('input', o.value)
            this.$emit('change', o.value, o)
        },
    },
}
</script>

<!-- Estilos del combobox y la bandera (globales, prefijo nf-): sirven fuera de /formulario. -->
<style src="./nfCombo.css"></style>
